import { NgIf } from '@angular/common';
import { Component, computed, effect, input, viewChild } from '@angular/core';
import { GeoJSONSourceComponent, LayerComponent, MarkerComponent, PopupComponent } from '@maplibre/ngx-maplibre-gl';
import { FastSvgComponent } from '@push-based/ngx-fast-svg';
import { Gateway } from '@wheere/api-site';
import { Antenna } from '@wheere/api-gateway';
import { applyMarkerOffset } from './marker-offset';

@Component({
  selector: 'lib-map-antenna-marker',
  standalone: true,
  imports: [MarkerComponent, PopupComponent, NgIf, FastSvgComponent, GeoJSONSourceComponent, LayerComponent],
  template: `
    @if (antenna().longitude && antenna().latitude && pseudo() && color() && show()) {
      <mgl-geojson-source
        [id]="'gateway-circle-' + antenna().id"
        [data]="getGatewayCircleData(antenna(), pseudo()!)"></mgl-geojson-source>
      <mgl-layer
        [id]="'gateway-circle-fill-' + antenna().id"
        [source]="'gateway-circle-' + antenna().id"
        type="fill"
        [paint]="{
          'fill-color': color(),
          'fill-opacity': 0.05,
        }">
      </mgl-layer>
      <mgl-layer
        [id]="'gateway-circle-line-' + antenna().id"
        [source]="'gateway-circle-' + antenna().id"
        type="line"
        [paint]="{
          'line-color': this.color(),
          'line-width': 2,
          'line-opacity': 0.8,
        }">
      </mgl-layer>
    }
    <ng-container *ngIf="hasPosition()">
      <mgl-marker #markerRef [lngLat]="[antenna().longitude!, antenna().latitude!]">
        <div class="relative flex flex-col items-center">
          @if (gwPower() !== undefined && gwPower() !== null) {
            <div class="mb-1 rounded bg-gray-800 px-2 py-0.5 text-xs font-semibold text-white shadow-md">
              {{ gwPower() }} dBm
              @if (snr() !== undefined && snr() !== null) {
                / {{ snr() }} dB
              }
            </div>
          }
          <button class="relative flex size-6" [style.color]="color() ? color() : '#ED107D'" [style.opacity]="1">
            <fast-svg name="gateway" size="100%"></fast-svg>
          </button>
        </div>
      </mgl-marker>

      <mgl-popup [offset]="16" [marker]="markerRef" [closeButton]="false" [maxWidth]="'250'">
        @if (gatewayName()) {
          <p class="mb-0.5 text-xs font-semibold uppercase tracking-wide text-gray-500">{{ gatewayName() }}</p>
        }
        <!-- Antenna N · RF OUT M — antenna number is the physical socket on the gateway
             (orderGateway), RF OUT is the resolved hardware pin (explicit override → fallback
             to orderGateway). Showing both lets the integrator double-check the wiring at a
             glance without opening the editor. -->
        <h4 class="text-base font-bold">Antenna {{ antenna().orderGateway }} · RF OUT {{ resolvedRfOut() }}</h4>
        <div class="mt-1 flex items-center gap-2">
          <i class="pi pi-map-marker text-primary-500"></i>
          <span class="text-sm">
            <span class="font-bold">Lat:</span> {{ antenna().latitude!.toFixed(5) }},
            <span class="font-bold">Lng:</span> {{ antenna().longitude!.toFixed(5) }}
          </span>
        </div>
      </mgl-popup>
    </ng-container>
  `,
})
export class MapAntennaMarkerComponent {
  antenna = input.required<Antenna>();
  color = input<string>();
  pseudo = input<number>();
  gwPower = input<number>();
  snr = input<number>();
  show = input<boolean>(false);
  /** Optional owning gateway name — when provided, the popup renders it as a small
   *  uppercase header above the "RF OUT N" line so the operator instantly knows which
   *  gateway the antenna belongs to (#16). Defaults to undefined so existing callers
   *  (tracker-live-map, inside-sites map-preview, etc.) keep their current popup. */
  gatewayName = input<string | undefined>(undefined);
  /**
   * Décalage écran (px) du marqueur posé au sol : le montre à son altitude sur une carte inclinée
   * avec un bâtiment 3D (ADR-017). `mgl-marker` ne relit pas son `offset` après création : il est
   * appliqué au marqueur MapLibre.
   */
  offset = input<[number, number] | null>(null);
  private readonly mglMarker = viewChild(MarkerComponent);

  constructor() {
    effect(() => {
      const offset = this.offset();
      const marker = this.mglMarker()?.markerInstance();
      applyMarkerOffset(marker, offset);
    });
  }

  /** Render the marker only when both coordinates are real numbers (excludes null / undefined,
   * keeps a valid 0). */
  protected readonly hasPosition = computed(
    () => typeof this.antenna().longitude === 'number' && typeof this.antenna().latitude === 'number'
  );

  /** Resolved RF OUT pin: explicit override on the antenna wins; otherwise fall back to
   * orderGateway (the positional default the firmware applies when rfOut is null). Keeps
   * the popup honest about what the hardware will actually do. */
  protected readonly resolvedRfOut = computed(() => {
    const a: Antenna = this.antenna();
    return a?.rfOut ?? a?.orderGateway ?? 0;
  });

  getGatewayCircleData(gateway: Gateway, radiusInMeters: number): GeoJSON.FeatureCollection {
    if (!gateway.longitude || !gateway.latitude || !radiusInMeters) {
      return {
        type: 'FeatureCollection',
        features: [],
      };
    }

    const center = [Number(gateway.longitude), Number(gateway.latitude)];
    const points = 64; // Number of points to create the circle
    const coordinates: number[][] = [];

    for (let i = 0; i <= points; i++) {
      const angle = (i * 360) / points;
      const point = this.calculatePointAtDistance(center[1], center[0], radiusInMeters, angle);
      coordinates.push([point.lng, point.lat]);
    }

    return {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [coordinates],
          },
          properties: {
            gatewayId: gateway.id,
            range: radiusInMeters,
          },
        },
      ],
    };
  }

  private calculatePointAtDistance(
    lat: number,
    lng: number,
    distance: number,
    bearing: number
  ): { lat: number; lng: number } {
    const R = 6371000;
    const bearingRad = this.toRadians(bearing);
    const latRad = this.toRadians(lat);
    const lngRad = this.toRadians(lng);

    const newLatRad = Math.asin(
      Math.sin(latRad) * Math.cos(distance / R) + Math.cos(latRad) * Math.sin(distance / R) * Math.cos(bearingRad)
    );

    const newLngRad =
      lngRad +
      Math.atan2(
        Math.sin(bearingRad) * Math.sin(distance / R) * Math.cos(latRad),
        Math.cos(distance / R) - Math.sin(latRad) * Math.sin(newLatRad)
      );

    return {
      lat: newLatRad * (180 / Math.PI),
      lng: newLngRad * (180 / Math.PI),
    };
  }

  private toRadians(degrees: number): number {
    return degrees * (Math.PI / 180);
  }
}
